
// Convert sigma score to percentile using error function approximation
export const sigmaToPercentile = (sigma: number): number => {
  const erf = (x: number): number => {
    const a1 =  0.254829592;
    const a2 = -0.284496736;
    const a3 =  1.421413741;
    const a4 = -1.453152027;
    const a5 =  1.061405429;
    const p  =  0.3275911;
    
    const sign = x >= 0 ? 1 : -1;
    x = Math.abs(x);
    
    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    
    return sign * y;
  };
  
  const percentile = 50 * (1 + erf(sigma / Math.sqrt(2)));
  return Math.round(percentile);
};

// Enhanced function to detect dual assessment structure
const isDualAssessmentData = (data: any): boolean => {
  return data && typeof data === 'object' && 
         (data.selfBehavior || data.workBehavior);
};

// Enhanced function to detect single behavior object with nested scores
const isSingleBehaviorWithScores = (data: any): boolean => {
  return data && typeof data === 'object' && 
         data.scores && typeof data.scores === 'object' &&
         !data.selfBehavior && !data.workBehavior;
};

// Helper function to get score directly from an object - ENHANCED
const getScoreDirectly = (obj: any, scoreKey: string): number | null => {
  if (!obj || typeof obj !== 'object') return null;
  
  // Try different possible key formats
  const possibleKeys = [
    scoreKey.toLowerCase(),
    scoreKey.toUpperCase(),
    scoreKey,
    `${scoreKey.toLowerCase()}_score`,
    `${scoreKey.toUpperCase()}_SCORE`
  ];
  
  for (const key of possibleKeys) {
    if (obj.hasOwnProperty(key) && obj[key] !== undefined && obj[key] !== null) {
      const value = Number(obj[key]);
      if (!isNaN(value)) {
        console.log(`Found valid score for ${scoreKey} with key ${key}:`, value);
        return value;
      }
    }
  }
  
  console.log(`No valid score found for ${scoreKey} in object:`, Object.keys(obj));
  return null;
};

// Helper function to extract score from a behavior object - ENHANCED
const getScoreFromBehaviorObject = (behaviorObj: any, scoreKey: string): number | null => {
  console.log('getScoreFromBehaviorObject called with:', { 
    behaviorObj: behaviorObj ? Object.keys(behaviorObj) : 'null', 
    scoreKey,
    hasScores: !!behaviorObj?.scores,
    hasDoubleNestedScores: !!behaviorObj?.scores?.scores
  });
  
  if (!behaviorObj || typeof behaviorObj !== 'object') {
    console.log('Invalid behavior object');
    return null;
  }
  
  // Check for double-nested scores structure first (scores.scores.objectivity)
  if (behaviorObj.scores && behaviorObj.scores.scores && typeof behaviorObj.scores.scores === 'object') {
    console.log('Found double-nested scores object:', behaviorObj.scores.scores);
    const scoreFromDoubleNested = getScoreDirectly(behaviorObj.scores.scores, scoreKey);
    if (scoreFromDoubleNested !== null) {
      console.log('Score found in double-nested scores:', scoreFromDoubleNested);
      return scoreFromDoubleNested;
    }
  }
  
  // Then check if there's a single nested scores object (dual assessment format)
  if (behaviorObj.scores && typeof behaviorObj.scores === 'object') {
    console.log('Found nested scores object:', behaviorObj.scores);
    const scoreFromNested = getScoreDirectly(behaviorObj.scores, scoreKey);
    if (scoreFromNested !== null) {
      console.log('Score found in nested scores:', scoreFromNested);
      return scoreFromNested;
    }
  }
  
  // Check directly on the behavior object (fallback)
  const directScore = getScoreDirectly(behaviorObj, scoreKey);
  console.log('Direct score result:', directScore);
  return directScore;
};

// ENHANCED function to extract from dual assessment structures
const extractFromDualAssessment = (data: any, scoreKey: string, preferSelf: boolean = true): number | null => {
  console.log('Extracting from dual assessment:', { data, scoreKey, preferSelf });
  
  // Try self behavior first if preferred, otherwise work behavior
  const primaryBehavior = preferSelf ? data.selfBehavior : data.workBehavior;
  const fallbackBehavior = preferSelf ? data.workBehavior : data.selfBehavior;
  
  // Check primary behavior first
  if (primaryBehavior) {
    const primaryScore = getScoreFromBehaviorObject(primaryBehavior, scoreKey);
    if (primaryScore !== null) {
      console.log('Found score in primary behavior:', primaryScore);
      return primaryScore;
    }
  }
  
  // Fallback to other behavior
  if (fallbackBehavior) {
    const fallbackScore = getScoreFromBehaviorObject(fallbackBehavior, scoreKey);
    if (fallbackScore !== null) {
      console.log('Found score in fallback behavior:', fallbackScore);
      return fallbackScore;
    }
  }
  
  console.log('No score found in dual assessment for:', scoreKey);
  return null;
};

// Parse scores from submission data - ENHANCED with better null handling
export const getScoreValue = (scores: any, scoreKey: string): number | null => {
  console.log('getScoreValue called with:', { scoreKey, scoresType: typeof scores, hasScores: !!scores });
  
  if (!scores) {
    console.log('No scores provided');
    return null;
  }
  
  // Check if this is dual assessment data
  if (isDualAssessmentData(scores)) {
    console.log('Detected dual assessment data structure');
    return extractFromDualAssessment(scores, scoreKey, true); // Prefer self behavior
  }
  
  // Handle regular single assessment format
  return getScoreDirectly(scores, scoreKey);
};

// Helper function to check if a behavior object has valid scores - ENHANCED
const hasValidScores = (behaviorObj: any): boolean => {
  if (!behaviorObj || typeof behaviorObj !== 'object') return false;
  
  const scoreKeys = ['dominance', 'extraversion', 'patience', 'formality', 'objectivity', 'A', 'B', 'C', 'D', 'E'];
  
  for (const key of scoreKeys) {
    const score = getScoreFromBehaviorObject(behaviorObj, key);
    // Accept any non-null number, including negative values and zero
    if (score !== null && typeof score === 'number' && !isNaN(score)) return true;
  }
  
  return false;
};

// Helper function to get default scores structure
const getDefaultScores = () => {
  return {
    aScore: 0,
    bScore: 0,
    cScore: 0,
    dScore: 0,
    eScore: null, // Use null instead of 0 to indicate missing data
    objectivityModel1: null,
    objectivityModel2: null,
    objectivityModel3: null,
    aPercentile: 50,
    bPercentile: 50,
    cPercentile: 50,
    dPercentile: 50,
    ePercentile: null, // Use null instead of 50 to indicate missing data
    objectivityModel1Percentile: null,
    objectivityModel2Percentile: null,
    objectivityModel3Percentile: null
  };
};

// Helper function to build the final scores result - ENHANCED with graceful degradation
const buildScoresResult = (
  aScore: number | null, 
  bScore: number | null, 
  cScore: number | null, 
  dScore: number | null, 
  eScore: number | null, 
  objectivityModel1: number | null, 
  objectivityModel2: number | null, 
  objectivityModel3: number | null
) => {
  // Check if we have valid scores for the main personality factors
  // Accept any numeric value (including 0 and negative numbers)
  const hasValidMainScores = 
    (typeof aScore === 'number' && !isNaN(aScore)) &&
    (typeof bScore === 'number' && !isNaN(bScore)) &&
    (typeof cScore === 'number' && !isNaN(cScore)) &&
    (typeof dScore === 'number' && !isNaN(dScore));
  
  if (!hasValidMainScores) {
    console.error('Invalid or missing scores detected, returning defaults:', { aScore, bScore, cScore, dScore });
    // Return default scores instead of throwing to prevent UI crashes
    return getDefaultScores();
  }

  const result = {
    aScore,
    bScore,
    cScore,
    dScore,
    eScore, // Keep null if no valid score
    objectivityModel1,
    objectivityModel2,
    objectivityModel3,
    aPercentile: sigmaToPercentile(aScore),
    bPercentile: sigmaToPercentile(bScore),
    cPercentile: sigmaToPercentile(cScore),
    dPercentile: sigmaToPercentile(dScore),
    ePercentile: eScore !== null ? sigmaToPercentile(eScore) : null,
    objectivityModel1Percentile: objectivityModel1 !== null ? sigmaToPercentile(objectivityModel1) : null,
    objectivityModel2Percentile: objectivityModel2 !== null ? sigmaToPercentile(objectivityModel2) : null,
    objectivityModel3Percentile: objectivityModel3 !== null ? sigmaToPercentile(objectivityModel3) : null
  };
  
  console.log('Final calculated scores:', result);
  return result;
};

// Calculate all behavioral scores from submission including dual objectivity models - ENHANCED
export const calculateBehavioralScores = (scores: any) => {
  console.log('calculateBehavioralScores called with:', { scores, type: typeof scores });
  
  // Check if this is a single behavior object with nested scores
  if (isSingleBehaviorWithScores(scores)) {
    console.log('Processing single behavior object with nested scores');
    
    // Extract scores directly from the nested scores object
    const scoresObj = scores.scores;
    const aScore = getScoreDirectly(scoresObj, 'dominance') ?? getScoreDirectly(scoresObj, 'A');
    const bScore = getScoreDirectly(scoresObj, 'extraversion') ?? getScoreDirectly(scoresObj, 'B');
    const cScore = getScoreDirectly(scoresObj, 'patience') ?? getScoreDirectly(scoresObj, 'C');
    const dScore = getScoreDirectly(scoresObj, 'formality') ?? getScoreDirectly(scoresObj, 'D');
    
    console.log('Extracted single behavior scores:', { aScore, bScore, cScore, dScore });
    
    // Calculate objectivity models - ENHANCED to prioritize actual scores
    const objectivityModel1 = getScoreDirectly(scoresObj, 'objectivityModel1') ?? 
      (dScore !== null && bScore !== null ? (dScore + (-bScore)) / 2 : null);
    const objectivityModel2 = getScoreDirectly(scoresObj, 'objectivityModel2') ?? 
      getScoreDirectly(scoresObj, 'objectivity');
    const objectivityModel3 = getScoreDirectly(scoresObj, 'objectivityModel3');
    
    // Model 3 gets priority, then Model 1, then Model 2, then null
    const eScore = objectivityModel3 !== null ? objectivityModel3 :
      (objectivityModel1 !== null ? objectivityModel1 :
        (objectivityModel2 !== null ? objectivityModel2 : null));
    
    return buildScoresResult(aScore, bScore, cScore, dScore, eScore, objectivityModel1, objectivityModel2, objectivityModel3);
  }
  
  // Handle dual assessment structure - ENHANCED
  if (isDualAssessmentData(scores)) {
    console.log('Processing dual assessment scores');
    
    // For dual assessments, extract from self behavior by default
    const selfBehavior = scores.selfBehavior;
    const workBehavior = scores.workBehavior;
    
    let primaryScores = selfBehavior;
    
    // If self behavior is null or doesn't have valid scores, try work behavior
    if (!selfBehavior || !hasValidScores(selfBehavior)) {
      console.log('Self behavior is null or has no valid scores, trying work behavior');
      primaryScores = workBehavior;
    }
    
    // If neither has valid scores, return defaults instead of throwing
    if (!primaryScores || !hasValidScores(primaryScores)) {
      console.error('Neither self nor work behavior has valid scores, returning defaults');
      return getDefaultScores();
    }
    
    // Extract scores from the chosen behavior using proper nested access
    const aScore = getScoreFromBehaviorObject(primaryScores, 'dominance') ?? getScoreFromBehaviorObject(primaryScores, 'A');
    const bScore = getScoreFromBehaviorObject(primaryScores, 'extraversion') ?? getScoreFromBehaviorObject(primaryScores, 'B');
    const cScore = getScoreFromBehaviorObject(primaryScores, 'patience') ?? getScoreFromBehaviorObject(primaryScores, 'C');
    const dScore = getScoreFromBehaviorObject(primaryScores, 'formality') ?? getScoreFromBehaviorObject(primaryScores, 'D');
    
    console.log('Extracted dual assessment scores:', { aScore, bScore, cScore, dScore });
    
    // Calculate objectivity models - ENHANCED to prioritize actual scores
    const objectivityModel1 = getScoreFromBehaviorObject(primaryScores, 'objectivityModel1') ?? 
      (dScore !== null && bScore !== null ? (dScore + (-bScore)) / 2 : null);
    const objectivityModel2 = getScoreFromBehaviorObject(primaryScores, 'objectivityModel2') ?? 
      getScoreFromBehaviorObject(primaryScores, 'objectivity');
    const objectivityModel3 = getScoreFromBehaviorObject(primaryScores, 'objectivityModel3');
    
    // Model 3 gets priority, then Model 1, then Model 2, then null
    const eScore = objectivityModel3 !== null ? objectivityModel3 :
      (objectivityModel1 !== null ? objectivityModel1 :
        (objectivityModel2 !== null ? objectivityModel2 : null));
    
    return buildScoresResult(aScore, bScore, cScore, dScore, eScore, objectivityModel1, objectivityModel2, objectivityModel3);
  }
  
  // Handle single assessment format (existing logic) - ENHANCED
  const aScore = getScoreValue(scores, 'dominance') ?? getScoreValue(scores, 'A');
  const bScore = getScoreValue(scores, 'extraversion') ?? getScoreValue(scores, 'B');
  const cScore = getScoreValue(scores, 'patience') ?? getScoreValue(scores, 'C');
  const dScore = getScoreValue(scores, 'formality') ?? getScoreValue(scores, 'D');

  console.log('Extracted single assessment scores:', { aScore, bScore, cScore, dScore });

  // Dual objectivity models - ENHANCED to prioritize actual scores
  const objectivityModel1 = getScoreValue(scores, 'objectivityModel1') ?? 
    (dScore !== null && bScore !== null ? (dScore + (-bScore)) / 2 : null);
  const objectivityModel2 = getScoreValue(scores, 'objectivityModel2') ?? getScoreValue(scores, 'objectivity');
  const objectivityModel3 = getScoreValue(scores, 'objectivityModel3');

  // Model 3 gets priority, then Model 1, then Model 2, then null
  const eScore = objectivityModel3 !== null ? objectivityModel3 :
    (objectivityModel1 !== null ? objectivityModel1 :
      (objectivityModel2 !== null ? objectivityModel2 : null));

  return buildScoresResult(aScore, bScore, cScore, dScore, eScore, objectivityModel1, objectivityModel2, objectivityModel3);
};
